import { ROVVideoStatus } from 'src/enums'
import {
  IAzureBlobUploadInitDto,
  IInitializeAzureBlobUploadDto,
  IInitializeROVVideoDto,
  IPendingCountDto,
  IROVVideoCreate,
  IROVVideoMove,
  IROVVideosWithSharedCategories,
  IROVVideoUpdate,
  IROVVideoUploadProviderDto,
  ISyncBatchRequest,
  ISyncProgressDto,
} from 'src/generated-types/rovvideo'
import { useDelete, useGet, usePost, usePut } from './reactQuery'

const ROV_TRANSCODING_POLL_MS = 10_000

export const rovVideosRefetchInterval = (data?: IROVVideosWithSharedCategories): number | false =>
  data?.videos?.some(video => video.status === ROVVideoStatus.Transcoding) ? ROV_TRANSCODING_POLL_MS : false

const baseUrl = (customerId: EntityId, localityId: EntityId) =>
  `customers/${customerId}/localities/${localityId}/rov-videos`

const adminBaseUrl = () => 'admin/rov-videos'

export const rovVideoAdminEndpoints = {
  startSync: () => `${adminBaseUrl()}/sync-old-videos`,
  getSyncProgress: () => `${adminBaseUrl()}/sync-progress`,
  getPendingCount: () => `${adminBaseUrl()}/sync-pending-count`,
}

export const rovVideosEndpoints = {
  createROVVideo(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  initializeBlobUpload(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/initialize-blob`
  },
  createVimeoPlaceholder(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/initialize`
  },
  getUploadProvider(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/upload-provider`
  },
  updateROVVideo(customerId: EntityId, localityId, id: EntityId) {
    return `${baseUrl(customerId, localityId)}/${id}`
  },
  deleteROVVideo(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  moveROVVideo(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId)}/${id}/move`
  },
}

export const useCreateROVVideo = (customerId: EntityId, localityId: EntityId) =>
  usePost<IROVVideoCreate, any>(rovVideosEndpoints.createROVVideo(customerId, localityId))

export const useInitializeAzureBlobUpload = (customerId: EntityId, localityId: EntityId) =>
  usePost<IAzureBlobUploadInitDto, IInitializeAzureBlobUploadDto>(
    rovVideosEndpoints.initializeBlobUpload(customerId, localityId),
  )

export const useCreateVimeoPlaceholder = (customerId: EntityId, localityId: EntityId) =>
  usePost<any, IInitializeROVVideoDto>(rovVideosEndpoints.createVimeoPlaceholder(customerId, localityId))

export const useGetROVVideoUploadProvider = (customerId: EntityId, localityId: EntityId) =>
  useGet<IROVVideoUploadProviderDto>(rovVideosEndpoints.getUploadProvider(customerId, localityId))

export const useUpdateROVVideo = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<IROVVideoUpdate, any>(rovVideosEndpoints.updateROVVideo(customerId, localityId, id))

// Moves a video that was uploaded on the wrong position or equipment. localityId is where the video
// sits now; the target in the body decides where it ends up, which may be another locality.
export const useMoveROVVideo = (customerId: EntityId, localityId: EntityId, id: EntityId) =>
  usePut<IROVVideoMove, any>(rovVideosEndpoints.moveROVVideo(customerId, localityId, id))

export const useDeleteROVVideo = (customerId: EntityId, localityId: EntityId) =>
  useDelete<any>(rovVideosEndpoints.deleteROVVideo(customerId, localityId))

export const useStartROVVideoSync = () => usePost<any, ISyncBatchRequest>(rovVideoAdminEndpoints.startSync())

export const useGetROVVideoSyncProgress = () => useGet<ISyncProgressDto>(rovVideoAdminEndpoints.getSyncProgress())

export const useGetROVVideoPendingCount = () => useGet<IPendingCountDto>(rovVideoAdminEndpoints.getPendingCount())
